import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export type ColorMode = 'light' | 'dark';

const STORAGE_KEY = 'dekon_theme';

function getInitial(): ColorMode {
    // The inline boot script in index.html already resolved and applied the
    // mode (to avoid a flash); trust that as the source of truth.
    const attr = document.documentElement.getAttribute('data-bs-theme');
    if (attr === 'light' || attr === 'dark') return attr;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ColorModeContextValue {
    mode: ColorMode;
    setMode: (mode: ColorMode) => void;
    toggle: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

export function ColorModeProvider({ children }: { children: ReactNode }) {
    const [mode, setMode] = useState<ColorMode>(getInitial);

    useEffect(() => {
        document.documentElement.setAttribute('data-bs-theme', mode);
        localStorage.setItem(STORAGE_KEY, mode);
    }, [mode]);

    const toggle = useCallback(() => {
        setMode((m) => (m === 'dark' ? 'light' : 'dark'));
    }, []);

    return (
        <ColorModeContext.Provider value={{ mode, setMode, toggle }}>
            {children}
        </ColorModeContext.Provider>
    );
}

export function useColorMode(): ColorModeContextValue {
    const ctx = useContext(ColorModeContext);
    if (!ctx) throw new Error('useColorMode must be used within a ColorModeProvider');
    return ctx;
}
